import { formatTokens, formatCost } from '@lens/lib/decode'
import type { ModelCostBreakdown } from '@lens/types/claude'
import { TableCell, TableRow } from '../../../table'
import { DataTable, type DataTableColumn } from '../../data-table'
import { shortModelName } from '../chart-primitives'

// Use the shared `shortModelName` (replaces the prior per-file `shortModel`
// implementation). The shared version returns "Opus 4.6" rather than
// "claude-opus-4.6" — model labels match other tables and charts now.
const shortModel = shortModelName

interface Props {
 models: ModelCostBreakdown[]
}

const columns: DataTableColumn<ModelCostBreakdown>[] = [
 {
  key: 'model',
  header:'Model',
  sort: (a, b) => shortModel(a.model).localeCompare(shortModel(b.model)),
  render: (m) =>shortModel(m.model),
 },
 {
  key: 'input',
  header:'Input',
  sort: (a, b) => a.input_tokens - b.input_tokens,
  defaultDesc: true,
  render: (m) =>formatTokens(m.input_tokens),
 },
 {
  key: 'output',
  header:'Output',
  sort: (a, b) => a.output_tokens - b.output_tokens,
  defaultDesc: true,
  render: (m) =>formatTokens(m.output_tokens),
 },
 {
  key: 'cacheWrite',
  header:'Cache Write',
  sort: (a, b) => a.cache_write_tokens - b.cache_write_tokens,
  defaultDesc: true,
  render: (m) =>formatTokens(m.cache_write_tokens),
 },
 {
  key: 'cacheRead',
  header:'Cache Read',
  sort: (a, b) => a.cache_read_tokens - b.cache_read_tokens,
  defaultDesc: true,
  render: (m) =>formatTokens(m.cache_read_tokens),
 },
 {
  key: 'cost',
  header:'Cost',
  sort: (a, b) => a.estimated_cost - b.estimated_cost,
  defaultDesc: true,
  render: (m) =>formatCost(m.estimated_cost),
 },
]

export function ModelTokenTable({ models }: Props) {
 const totals = models.reduce(
  (acc, m) => ({
   input: acc.input + m.input_tokens,
   output: acc.output + m.output_tokens,
   cacheWrite: acc.cacheWrite + m.cache_write_tokens,
   cacheRead: acc.cacheRead + m.cache_read_tokens,
   cost: acc.cost + m.estimated_cost,
  }),
  { input: 0, output: 0, cacheWrite: 0, cacheRead: 0, cost: 0 },
 )

 return (
  <DataTable
   wrapInCard={false}
   columns={columns}
   rows={models}
   rowKey={(m) => m.model}
   initialSort={{ key: 'cost', dir: 'desc' }}
   empty="No model usage"
   footerRow={() => (
    <TableRow className="border-t border-border bg-muted/30">
     <TableCell className="font-normal">Total</TableCell>
     <TableCell className="text-right tabular-nums">{formatTokens(totals.input)}</TableCell>
     <TableCell className="text-right tabular-nums">{formatTokens(totals.output)}</TableCell>
     <TableCell className="text-right tabular-nums">{formatTokens(totals.cacheWrite)}</TableCell>
     <TableCell className="text-right tabular-nums">{formatTokens(totals.cacheRead)}</TableCell>
     <TableCell className="text-right tabular-nums">{formatCost(totals.cost)}</TableCell>
    </TableRow>
   )}
  />
 )
}
